import { captureService } from '@/services/capture-service'
import { VideoFrameSource, type SourceFrame } from '@/services/video-frame-source'
import type { BuffDefinition, BuffObservation, BuffPreferences } from './types'
import { defaultBuffRegion } from './types'
import type { BuffPixels, BuffVisionProfile } from './vision-core.js'

export interface BuffResult { observations: BuffObservation[]; quality: string; processingMs: number; reads: unknown; evidence?: unknown; wasmHeapBytes?: number; capturedAt: number; sourceCapturedAt?: number; sentAt?: number; sourceObservedAt?: number; timestampSource?: SourceFrame['timestampSource']; captureMs?: number; frameId?: number; sourceId?: number; captureIntervalMs?: number; skippedBusy?: number }
export class BuffMonitor {
  private worker: Worker | null = null
  private generation = 0
  private ready = false
  private busy = false
  private request = 0
  private capturedAt = 0
  private lastSent = 0
  private freshAt = 0
  private sequence = 0
  private lastSequence = -1
  private source: VideoFrameSource | null = null
  private sentFrame: SourceFrame | null = null
  private video: HTMLVideoElement | null = null
  private watchdog: ReturnType<typeof setInterval> | null = null
  private suspended = true
  private stopped = true
  private region: BuffPreferences['region'] = null
  private origin = { x: 0, y: 0 }
  private sourceId = -1
  private sentShape = ''
  private initializeReject: ((error: Error) => void) | null = null
  private baseIntervalMs = 500
  private intervalMs = 500
  private canvas: OffscreenCanvas | null = null
  private captureMs = 0
  private priorFrameAt = 0
  private captureIntervalMs = 0
  private skippedBusy = 0
  private busySequence = -1
  private retryTimer: ReturnType<typeof setTimeout> | null = null
  private recoveryAttempts = 0
  private healthySince = 0
  private definitions: BuffDefinition[] = []
  private previous: BuffObservation[] = []
  private profile: BuffVisionProfile = 'default'
  constructor(private readonly result: (result: BuffResult) => void, private readonly unavailable: (reason: string) => void, private readonly fatal: (error: string) => void, private readonly now: () => number = Date.now) {}
  setInterval(baseIntervalMs: number, effectiveIntervalMs: number): void {
    const changed = this.baseIntervalMs !== baseIntervalMs
    this.baseIntervalMs = baseIntervalMs; this.intervalMs = effectiveIntervalMs
    if (changed && this.worker) this.worker.postMessage({ type: 'cadence', generation: this.generation, intervalMs: baseIntervalMs })
  }

  async start(definitions: BuffDefinition[], region: BuffPreferences['region'], previous: BuffObservation[] = [], recovering = false, profile: BuffVisionProfile = 'default'): Promise<void> {
    this.stop()
    if (profile !== this.profile) previous = []
    this.profile = profile
    if (!recovering) this.recoveryAttempts = 0
    this.definitions = definitions; this.previous = previous
    this.stopped = false
    this.lastSent = -Infinity; this.lastSequence = -1
    this.region = region ?? defaultBuffRegion()
    const generation = ++this.generation
    this.sourceId = captureService.selectionId
    const icons: BuffPixels[] = []
    for (const definition of definitions.filter(item => item.enabled)) {
      const bitmap = await createImageBitmap(definition.image)
      try {
        const canvas = new OffscreenCanvas(bitmap.width, bitmap.height)
        const context = canvas.getContext('2d', { willReadFrequently: true })!
        context.drawImage(bitmap, 0, 0)
        icons.push({ id: definition.id, width: bitmap.width, height: bitmap.height, pixels: context.getImageData(0, 0, bitmap.width, bitmap.height).data })
      } finally { bitmap.close() }
    }
    if (this.stopped || generation !== this.generation) return
    const worker = new Worker(new URL('./monitor.worker.ts', import.meta.url), { type: 'module' })
    this.worker = worker
    await new Promise<void>((resolve, reject) => {
      // Allow slow worker transfers over dev port forwarding without changing production.
      const timeout = import.meta.env.DEV ? undefined
        : setTimeout(() => reject(new Error('BUFF engine startup timed out')), 60_000)
      this.initializeReject = error => { clearTimeout(timeout); reject(error) }
      worker.onerror = event => {
        if (generation !== this.generation || this.stopped) return
        clearTimeout(timeout)
        if (this.ready) this.recover(event.message || 'BUFF worker failed')
        else reject(new Error(event.message || 'BUFF worker failed'))
      }
      worker.onmessage = event => {
        if (generation !== this.generation || this.stopped) return
        const message = event.data
        if (message.type === 'pulse') { this.pulse(); return }
        if (message.generation !== generation) return
        if (message.type === 'ready') { clearTimeout(timeout); this.initializeReject = null; this.ready = true; this.attachVideo(); resolve(); return }
        if (message.type === 'error') { clearTimeout(timeout); this.busy = false; if (this.ready) this.recover(message.error); else reject(new Error(message.error)); return }
        if (message.type !== 'result' || message.id !== this.request) return
        this.busy = false
        if (this.sourceId !== captureService.selectionId || this.sentShape !== `${this.video?.videoWidth},${this.video?.videoHeight}` || this.suspended || this.now() - this.lastSent > 2000 || this.now() - (this.sentFrame?.sourceCapturedAt ?? this.capturedAt) > Math.max(2000, this.baseIntervalMs + 1000) || this.now() - this.freshAt > Math.max(1500, this.baseIntervalMs + 1000)) { this.suspend('result-stale'); return }
        const observations = message.observations as BuffObservation[]
        this.previous = structuredClone(observations)
        this.healthySince ||= this.now()
        if (this.now() - this.healthySince >= 30_000) this.recoveryAttempts = 0
        for (const observation of observations) if (observation.box) { observation.box = [...observation.box]; observation.box[1] += this.origin.x; observation.box[2] += this.origin.y }
        this.result({ observations, quality: message.quality, processingMs: message.processingMs, reads: message.reads, evidence: message.evidence, capturedAt: this.capturedAt, sourceCapturedAt: this.sentFrame?.sourceCapturedAt, sentAt: this.lastSent, sourceObservedAt: this.sentFrame?.observedAt, timestampSource: this.sentFrame?.timestampSource, wasmHeapBytes: message.wasmHeapBytes, captureMs: this.captureMs, frameId: this.lastSequence, sourceId: this.sourceId, captureIntervalMs: this.captureIntervalMs, skippedBusy: this.skippedBusy })
      }
      worker.postMessage({ type: 'initialize', generation, icons, previous, intervalMs: this.baseIntervalMs, profile }, icons.map(icon => icon.pixels.buffer))
    })
  }
  pulse(): void {
    if (this.stopped || !this.ready) return
    if (this.video !== captureService.video || this.sourceId !== captureService.selectionId) {
      this.sourceId = captureService.selectionId; this.request++; this.busy = false; this.previous = []
      this.worker?.postMessage({ type: 'source', generation: this.generation })
      this.attachVideo(); this.suspend('source-changed')
    }
    const video = this.video
    const track = captureService.mediaStream?.getVideoTracks()[0]
    if (!video || captureService.status !== 'active' || !track || track.readyState !== 'live' || track.muted || video.paused || !video.videoWidth || video.readyState < 2) { this.suspend('capture-unavailable'); return }
    const frame = this.source?.read()
    if (frame) { this.sequence = frame.id; this.freshAt = frame.sourceCapturedAt ?? frame.capturedAt }
    if (this.now() - this.freshAt > Math.max(1500, this.baseIntervalMs + 1000)) { this.suspend('frame-stale'); return }
    if (this.busy) {
      if (this.sequence !== this.busySequence) { this.skippedBusy++; this.busySequence = this.sequence }
      if (this.now() - this.lastSent > 2000) this.unavailable('result-stale')
      if (this.now() - this.lastSent > 3000) this.recover('BUFF recognition timed out')
      return
    }
    // Coarse capture and polling clocks drift independently. Scheduling from
    // dispatch time can miss each newly delivered frame and then send it one
    // period late. Follow source cadence, while bounding catch-up dispatches.
    const dispatchElapsed = this.now() - this.lastSent
    const elapsed = frame && this.intervalMs > 500
      ? Math.max(dispatchElapsed, (frame.sourceCapturedAt ?? frame.capturedAt) - (this.sentFrame?.sourceCapturedAt ?? this.capturedAt))
      : dispatchElapsed
    // Capture timestamps jitter slightly around a coarse interval. Skipping a
    // nearly due fresh frame would cost a whole interval and expire icon evidence.
    const cadenceTolerance = this.intervalMs > 500 ? 50 : 0
    if (!frame || this.sequence === this.lastSequence || elapsed + cadenceTolerance < this.intervalMs
      || this.now() - this.lastSent < Math.min(500, this.intervalMs)) return
    if (this.suspended) { this.worker?.postMessage({ type: 'resume', generation: this.generation }); this.suspended = false }
    try {
      const region = this.region
      const x = region ? Math.floor(region.x * video.videoWidth) : 0
      const y = region ? Math.floor(region.y * video.videoHeight) : 0
      const width = region ? Math.min(video.videoWidth - x, Math.round(region.width * video.videoWidth)) : video.videoWidth
      const height = region ? Math.min(video.videoHeight - y, Math.round(region.height * video.videoHeight)) : video.videoHeight
      if (width < 16 || height < 16) { this.suspend('region-too-small'); return }
      const started = this.now()
      const canvas = this.canvas ??= new OffscreenCanvas(video.videoWidth, video.videoHeight)
      if (canvas.width !== video.videoWidth) canvas.width = video.videoWidth
      if (canvas.height !== video.videoHeight) canvas.height = video.videoHeight
      const context = canvas.getContext('2d', { willReadFrequently: true })!
      context.drawImage(video, 0, 0)
      const pixels = context.getImageData(0, 0, video.videoWidth, video.videoHeight).data
      this.captureMs = this.now() - started
      this.captureIntervalMs = this.priorFrameAt ? started - this.priorFrameAt : 0; this.priorFrameAt = started
      this.sentShape = `${video.videoWidth},${video.videoHeight}`; this.origin = { x, y }; this.busy = true; this.capturedAt = frame.capturedAt; this.lastSent = started; this.lastSequence = this.sequence; this.sentFrame = frame
      this.worker?.postMessage({ type: 'frame', generation: this.generation, id: ++this.request, capturedAt: this.capturedAt, pixels, width: video.videoWidth, height: video.videoHeight, region: { x, y, width, height } }, [pixels.buffer])
    } catch { this.busy = false; this.request++; this.suspend('capture-unavailable') }
  }
  private attachVideo(): void {
    this.source?.dispose(); this.video = captureService.video; this.freshAt = this.now(); this.lastSequence = -1
    this.source = new VideoFrameSource(this.video, () => Math.max(1500, this.baseIntervalMs + 1000), { now: this.now, onFrame: () => this.pulse() })
    if (this.watchdog === null) this.watchdog = setInterval(() => this.pulse(), 250)
  }
  private suspend(reason: string): void { this.suspended = true; this.unavailable(reason) }
  private recover(reason: string): void {
    if (this.stopped || this.retryTimer !== null) return
    this.worker?.terminate(); this.worker = null; this.generation++; this.ready = false; this.busy = false; this.healthySince = 0
    if (this.recoveryAttempts >= 3) { this.fatal(reason); return }
    this.suspend('worker-recovering')
    const delay = 1000 * 2 ** this.recoveryAttempts++
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null
      const previous = this.sourceId === captureService.selectionId ? this.previous : []
      void this.start(this.definitions, this.region, previous, true, this.profile).catch(error => this.recover(String(error)))
    }, delay)
  }
  stop(): void {
    this.stopped = true; this.generation++; this.ready = false; this.busy = false; this.suspended = true
    this.initializeReject?.(new Error('BUFF initialization cancelled')); this.initializeReject = null
    this.worker?.terminate(); this.worker = null
    if (this.retryTimer !== null) clearTimeout(this.retryTimer)
    this.retryTimer = null; this.canvas = null; this.priorFrameAt = 0; this.skippedBusy = 0
    this.source?.dispose(); this.source = null; this.sentFrame = null; this.video = null
    if (this.watchdog !== null) clearInterval(this.watchdog)
    this.watchdog = null
  }
}
